import { useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import type { VolumeRunRow } from '../grids/VolumeRunsGrid';

interface VolumeRunSidePanelProps {
  item: VolumeRunRow | null;
  loading?: boolean;
  variant?: 'panel' | 'page';
  onClose: () => void;
  onBack?: () => void;
  onOpenFullPage?: (id: string) => void;
  onResolve?: (id: string, gravity?: number) => void;
  onRebook?: (id: string, productionDate: string) => void;
  busy?: boolean;
}

export function VolumeRunSidePanel({
  item,
  loading = false,
  variant = 'panel',
  onClose,
  onBack,
  onOpenFullPage,
  onResolve,
  onRebook,
  busy
}: VolumeRunSidePanelProps): ReactElement {
  const isPage = variant === 'page';
  const [rebookDate, setRebookDate] = useState(item?.productionDate ?? '');
  const [gravity, setGravity] = useState(item?.gravity != null ? String(item.gravity) : '');
  return (
    <aside className={`side-panel${isPage ? ' side-panel--page' : ''}`} aria-label="Volume run">
      <div className="side-panel-header">
        <div className="side-panel-title-row">
          {isPage && onBack && (
            <button type="button" className="secondary panel-back-btn" onClick={onBack}>
              ← Back to list
            </button>
          )}
          <h3>{item ? `${item.periodId} ${item.product}` : 'Volume run'}</h3>
        </div>
        <div className="side-panel-actions">
          {!isPage && item && onOpenFullPage && (
            <button type="button" className="secondary" onClick={() => onOpenFullPage(item.id)}>
              Open full page
            </button>
          )}
          <button type="button" className="secondary" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
      </div>
      {loading && <p className="hint">Loading…</p>}
      {!loading && !item && <p>Select a volume run.</p>}
      {item && (
        <div className="side-panel-body">
          <p>
            <span className="status-pill">{item.product}</span> {item.gross} {item.unit} on {item.productionDate}
          </p>
          <p>
            <Link
              className="secondary"
              data-testid="show-accounting-transactions"
              to={`/accounting/books?volumeRun=${encodeURIComponent(item.id)}`}
            >
              Show accounting transactions
            </Link>
          </p>
          <p className="hint">
            Allocated on the selected contract DOI as-of the production date. Settlement {item.settlementPrice != null ? `$${item.settlementPrice.toFixed(2)}` : 'is not resolved yet'}.
            Royalty inherits that price. Resolve picks the T-20 formula in force on the production date (gravity/product
            picker, then Default).
            {item.settlementFormulaName ? ` Last pick: ${item.settlementFormulaName}.` : ''}
          </p>
          {onResolve && (
            <label>
              API gravity (optional)
              <input
                data-testid="run-gravity"
                value={gravity}
                onChange={(e) => setGravity(e.target.value)}
                placeholder="blank = Default formula"
              />
              <button
                type="button"
                data-testid="resolve-settlement"
                disabled={busy}
                onClick={() => onResolve(item.id, gravity ? Number(gravity) : undefined)}
              >
                Resolve offtake settlement
              </button>
            </label>
          )}
          {onRebook && (
            <label>
              New production date
              <input type="date" value={rebookDate} onChange={(e) => setRebookDate(e.target.value)} />
              <button
                type="button"
                className="secondary"
                data-testid="rebook-volume"
                disabled={busy || !rebookDate}
                onClick={() => onRebook(item.id, rebookDate)}
              >
                Reverse / rebook
              </button>
            </label>
          )}
          <table className="requests-grid">
            <thead>
              <tr>
                <th>Class</th>
                <th>Decimal</th>
                <th>Allocated</th>
              </tr>
            </thead>
            <tbody>
              {item.lines.map((line) => (
                <tr key={`${line.partyId}-${line.interestClass}`}>
                  <td>{line.interestClass}</td>
                  <td className="mono-id">{line.decimal}</td>
                  <td>
                    {line.allocatedQuantity} {item.unit}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </aside>
  );
}
